import MapManager from '../../../../services/map/MapManager';
import { HereMapsService } from '../../../../services/map';
import {
  CENTER_LOCATION, createStopTypeMarker, createStopBehaviourMarker, findBoundingBox, FIT_GROUP,
  createBubbleContent, createPinMarker,
} from '../../../../services/map/drawing';
import { MapEventType } from '../../../../services/map/mapEventDispatcher';
import theme from '../../../../theme';
import createStopInfoBubble from '../../../../services/map/drawing/infobubbles/stopInfoBubbleCreator';
import NOP from '../../../../utils/NOP';
import { info } from '../../../../services/Logging';
import LastTraceRenderer from '../../../Vehicles/Components/Map/LastTraceRenderer';
import { getStopType } from '../../../../types/mappers';
import {
  anyStopHasBehaviourTags, behaviourGlyphTag, isCompositeBehaviour,
} from '../../../../types/stopBehaviour.type';

const { H } = HereMapsService;

const followCircleStyle = {
  color: theme.color.lastRouteStroke,
  size: 14,
};

const stopIconStyle = {
  normal: {
    fillColor: theme.color.stop.normal,
  },
  hover: {
    fillColor: theme.color.stop.hover,
  },
};

/*
 * Stop markers have always been drawn at a flat zIndex of 2 - the same value the vehicle pin uses -
 * so two stops at one coordinate stack in whatever order they happened to be added, and the lower
 * one is unreachable.
 *
 * A per-sequence zIndex fixes that deterministically, but it also lifts stops above the pin, which
 * is a visible change. It is therefore applied only to orders that actually carry behaviour labels:
 * co-located stops are what the labels introduce, and an untagged order must look exactly as it does
 * today.
 */
const STOP_Z_INDEX = 2;
// Above MAP_MARKERS_Z_INDEX (20000) and below MAP_CLUSTER_Z_INDEX (30000): a selected stop must win
// against every other marker without covering a cluster badge.
const STOP_EMPHASIS_Z_INDEX = 25000;

/*
 * `drawOrder` is the stop's dense position in the already-filtered, already-sorted draw list, NOT
 * the server-supplied `sequence`. Sequence cannot deconflict co-located markers: it is 0-based after
 * order.mapper renumbering, can arrive as a string, and can be missing, negative or duplicated -
 * every one of which collapses to the same z-index and puts the markers back in insertion order.
 */
const stopZIndex = (drawOrder, perSequence) => (
  perSequence ? STOP_Z_INDEX + drawOrder : STOP_Z_INDEX
);

const defaultLastTraceConfig = {
  trace: false, mask: false, closeButton: false, vehicleLink: false,
};
class VehicleDetailsMapManager extends MapManager {
  constructor({ refId, t, fetchLocation }) {
    super({
      refId, fullscreen: true, legend: false, t, fetchLocation,
    });
    this.initCenterAtLocation = true;
    this.route = null;
    this.historicRoute = null;
    this.routeShadow = null;
    this.routeFollowCircle = null;
    this.stops = null;
    this.lastTraceRenderer = null;
    this.drawnCrowns = [];
    this.fetchLocation = fetchLocation;
  }

  drawCrowns(geofencingData) {
    if (!geofencingData) {
      return;
    }

    geofencingData.stops.forEach((stop) => {
      if (this.drawnCrowns.includes(stop.stopId)) {
        return;
      }

      this.drawnCrowns.push(stop.stopId);
      const latLngAlt = stop.polygon.flatMap((point) => [point.lat, point.lng, 100]);
      const lineString = new H.geo.LineString(latLngAlt);

      const crown = new H.map.Polygon(lineString, {
        style: {
          fillColor: theme.color.primaryLowOpacity,
          strokeColor: theme.color.primary,
          lineWidth: 3,
          zIndex: 1,
        },
      });

      this.addToMap(crown);
    });
  }

  drawEta(eta) {
    const { routeAsOneLine } = this.createRoute(eta.path, {
      style: {
        strokeColor: theme.color.routeStroke,
      },
      zIndex: 1200,
      volatility: true,
    });
    this.addToMap(routeAsOneLine);

    return () => {
      this.removeFromMap(routeAsOneLine);
    };
  }

  drawStops(stops) {
    const isStopDrawable = (stop) => stop && stop.latitude && stop.longitude;
    if (!stops) {
      return NOP;
    }

    // Order-wide, so the whole map uses one consistent stacking rule.
    const perSequenceZIndex = anyStopHasBehaviourTags(stops);
    const stopsMarkers = stops
      .filter(isStopDrawable)
      .map((stop, drawOrder) => this.createStopMarkerWithBubble(stop, perSequenceZIndex, drawOrder));

    this.stops = stopsMarkers;
    stopsMarkers.forEach(({ apply }) => apply(this.ui));
    return () => {
      stopsMarkers.forEach(({ dispose }) => dispose(this.ui));
      this.stops = null;
    };
  }

  createStopMarkerWithBubble(stop, perSequenceZIndex = false, drawOrder = 0) {
    const type = getStopType(stop);
    const position = { lat: stop.latitude, lng: stop.longitude };
    const infoBubble = createStopInfoBubble(stop, position, type, this.t);
    const onTap = () => {
      if (infoBubble.getState() === 'open') {
        infoBubble.close();
      } else {
        // eslint-disable-next-line no-unused-expressions
        this.stops && this.stops.forEach((s) => s.closeBubble());
        infoBubble.open();
      }
    };
    const baseZIndex = stopZIndex(drawOrder, perSequenceZIndex);
    const markerConfig = {
      data: { clickable: true },
      zIndex: baseZIndex,
      volatility: true,
      onTap,
    };
    /*
     * behaviourGlyphTag applies the display precedence: it is undefined for an operational stop -
     * including every untagged one - which keeps the load/unload glyph it has always had. That is
     * what makes an untagged order's map pixel-identical.
     */
    const glyphTag = behaviourGlyphTag(stop);
    const marker = glyphTag
      ? createStopBehaviourMarker(
        glyphTag,
        stop.sequence,
        position,
        stopIconStyle.normal,
        markerConfig,
        { composite: isCompositeBehaviour(stop) },
      )
      : createStopTypeMarker(type, stop.sequence, position, stopIconStyle.normal, markerConfig);

    return {
      id: stop.id,
      position,
      /*
       * Emphasis-on-select, so a stop sharing a coordinate with another can still be brought to the
       * front. Behind the SAME gate as the per-sequence z-index: without it, tapping a stop on an
       * untagged order would lift that marker above the vehicle pin and the trace markers, which is
       * a visible change to an order carrying no labels at all.
       */
      setEmphasis: (emphasised) => {
        if (!perSequenceZIndex) {
          return;
        }
        marker.setZIndex(emphasised ? STOP_EMPHASIS_Z_INDEX : baseZIndex);
      },
      apply: (ui) => {
        ui.getMap().addObject(marker);
        ui.addBubble(infoBubble);
      },
      dispose: (ui) => {
        ui.getMap().removeObject(marker);
        ui.removeBubble(infoBubble);
      },
      hideInfo: () => {
        infoBubble.close();
      },
      onTap,
      closeBubble: () => infoBubble.close(),
    };
  }

  createPinMarkerWithBubble(pos) {
    const position = { lat: pos.lat, lng: pos.lng };
    const infoBubble = new H.ui.InfoBubble(position);
    infoBubble.addClass('vehicle-details-infobubble');
    infoBubble.addClass('above-marker-infobubble');
    const bubbleOpener = createBubbleContent(
      this.ui,
      this.t,
      this.fetchLocation,
      this.showLastTrace,
      infoBubble,
      true,
    );
    bubbleOpener(pos);
    infoBubble.close();

    const onTap = () => {
      if (infoBubble.getState() === 'open') {
        infoBubble.close();
      } else {
        infoBubble.open();
      }
    };
    const marker = createPinMarker(position, {
      data: { vehicle: pos.vehicle, clickable: true }, zIndex: 2, volatility: true, onTap,
    });

    return {
      id: 'end',
      position,
      apply: (ui) => {
        ui.getMap().addObject(marker);
        ui.addBubble(infoBubble);
      },
      dispose: (ui) => {
        ui.getMap().removeObject(marker);
        ui.removeBubble(infoBubble);
      },
      hideInfo: () => {
        infoBubble.close();
      },
      onTap,
      closeBubble: () => infoBubble.close(),
    };
  }

  drawHistoryBoundaryMarkers(routePoints, order) {
    const a = routePoints[0];
    const b = routePoints[Math.max(0, routePoints.length - 1)];

    const markers = this.createHistoryBoundaryMarkers(a, undefined);
    const markerEnd = this.createPinMarkerWithBubble({ ...b, order });

    if (markers) {
      markerEnd.apply(this.ui);
      this.addAllToMap(markers);
      return () => {
        this.removeAllFromMap(markers);
        markerEnd.dispose(this.ui);
      };
    }
    return NOP;
  }

  drawLastPositionMarker(lastPosition, data) {
    if (!lastPosition) {
      return NOP;
    }
    const marker = this.createPinMarker(lastPosition, { data });
    if (marker) {
      this.addToMap(marker);
      const location = { lat: lastPosition.coordinateLatitude, lng: lastPosition.coordinateLongitude };
      const zoomFactor = 10;
      if (this.initCenterAtLocation) {
        this.zoomTo(location, zoomFactor);
        this.initCenterAtLocation = false;
      }
      this.mapEventDispatcher.dispatch({
        type: MapEventType.NEW_BOUNDING,
        payload: {
          location,
          zoom: zoomFactor,
          type: CENTER_LOCATION,
        },
      });
      return () => this.removeFromMap(marker);
    }
    return NOP;
  }

  centerToRouteAndStops = () => {
    const points = [];
    if (this.route && this.route.routeAsOneLine) {
      const routeBounding = this.route.routeAsOneLine.getBoundingBox();
      points.push(routeBounding.getBottomRight(), routeBounding.getTopLeft());
    }

    if (this.stops) {
      this.stops.forEach(({ position }) => points.push(position));
    }

    const bounding = points.length > 0 ? findBoundingBox(points) : null;

    if (bounding) {
      this.mapEventDispatcher.dispatch({
        type: MapEventType.NEW_BOUNDING,
        payload: {
          bounding,
          triggerCentering: true,
          type: FIT_GROUP,
        },
      });
    }
  }

  drawRouteWithShadow(points, { onTap } = {}) {
    const route = this.drawRoute(points, {
      onTap: (t) => {
        if (t && t.index) {
          onTap(t.index);
        }
      },
      visibility: false,
    });
    const { routeAsOneLine: highlight } = this.createRoute(points, {
      style: {
        strokeColor: theme.color.lastRouteShadow,
      },
      zIndex: 200,
      volatility: true,
      visibility: false,
      onTap: (t) => {
        if (t && t.index) {
          onTap(t.index);
        }
      },
    });
    this.route = route;
    this.addToMap(highlight);
    return () => {
      this.removeFromMap(route.routeAsOneLine);
      this.removeFromMap(highlight);
      this.route = null;
    };
  }

  drawHistoricRoute(points, config) {
    const { routeAsOneLine } = this.createRoute(points, {
      style: {
        strokeColor: theme.color.propableHistoricRoute,
      },
      zIndex: 200,
      visibility: true,
      ...config,
    });

    this.historicRoute = routeAsOneLine;

    this.addToMap(routeAsOneLine);

    return () => {
      this.removeFromMap(routeAsOneLine);
    };
  }

  drawPlayFocusDot({ lat, lng }) {
    const circle = this.createCircleMarker({
      coordinateLatitude: lat,
      coordinateLongitude: lng,
    }, followCircleStyle, { volatility: true });
    this.addToMap(circle);
    this.routeFollowCircle = circle;
    return () => {
      this.routeFollowCircle = null;
      this.removeFromMap(circle);
    };
  }

  drawRoute(points, config) {
    const { routeAsSeparatedLines, routeAsOneLine } = this.createRoute(points, config);
    this.addToMap(routeAsOneLine);
    return { routeAsSeparatedLines, routeAsOneLine };
  }

  resetInitialCenter() {
    this.initCenterAtLocation = true;
  }

  showWholeRoute() {
    if (this.route) {
      this.route.routeAsOneLine.setVisibility(false);
    }
  }

  toggleHistoricRoute(value) {
    if (this.historicRoute) {
      this.historicRoute.setVisibility(value);
    }
    if (this.route) {
      this.route.routeAsOneLine.setVisibility(!value);
    }
  }

  clearMap() {
    this.routeFollowCircle = null;
    super.clearMap();
    info('Clearing map');
  }

  drawCurrentLocation(index) {
    if (this.route && this.route.routeAsSeparatedLines) {
      const point = this.route.routeAsSeparatedLines[Math.max(0, index)];
      if (point) {
        const { lat, lng } = point.getData();
        this.updateRouteFollowCircle(lat, lng);
      }
    }
  }

  updateRouteFollowCircle(lat, lng) {
    if (this.routeFollowCircle) {
      this.routeFollowCircle.setGeometry({
        lat,
        lng,
      });
    }
  }

  renderTraceStatistics({ vehicle, statistics, config = {} }) {
    this.lastTraceRenderer = new LastTraceRenderer({
      mapEventDispatcher: this.mapEventDispatcher,
      vehicle,
      statistics,
      ui: this.ui,
      t: this.t,
      onDispose: () => {
        this.mapEventDispatcher.dispatch({
          type: MapEventType.NEW_BOUNDING,
          payload: {
            bounding: findBoundingBox(this.dataPoints),
          },
        });
        this.lastTraceRenderer = null;
      },
      onApply: () => {
        this.closeAllBubbles();
      },
      config: {
        ...defaultLastTraceConfig, ...config,
      },
    });
    return this.lastTraceRenderer.apply();
  }

  tapEventIcon({ eventType, eventId }) {
    switch (eventType) {
      case 'break':
        if (this.lastTraceRenderer) {
          // eslint-disable-next-line no-unused-expressions
          this.stops && this.stops.forEach((s) => s.closeBubble());
          this.lastTraceRenderer.tapMarker(eventId, true);
        }
        break;
      case 'stop': {
        const stop = this.stops && this.stops.find(({ id }) => id === eventId);
        if (stop) {
          // The 'break' arm above guards this; this one did not, and tapping a stop before the
          // trace renderer exists threw.
          if (this.lastTraceRenderer) {
            this.lastTraceRenderer.closeInfoBubbles();
          }
          this.stops.forEach((s) => s.setEmphasis(s.id === eventId));
          stop.onTap();
          this.zoomTo(stop.position, false, true);
        }
        break;
      }
      default:
        break;
    }
  }
}

export default VehicleDetailsMapManager;
